import React, { useEffect, useRef } from 'react';

// Renders admin-authored page HTML inside a Shadow DOM so the page's own <style> blocks stay
// contained. Injected straight into the app document (dangerouslySetInnerHTML), those styles
// are global and bleed into the app chrome — most visibly, they restyle and resize the top
// bar. A shadow root encapsulates them: styles inside don't escape, and the menu holds still.
//
// This keeps rendering INLINE — same document, no iframe, no CORS (see README C2). It is not
// the iframe that decision rejected; it's the same inline HTML, just style-isolated.
interface ShadowContentProps {
  html: string;
}

const ShadowContent: React.FC<ShadowContentProps> = ({ html }) => {
  const hostRef = useRef<HTMLDivElement>(null);
  const shadowRef = useRef<ShadowRoot | null>(null);

  useEffect(() => {
    const host = hostRef.current;
    if (!host) {
      return;
    }
    // attachShadow may run only once per element; reuse the root on subsequent updates.
    if (!shadowRef.current) {
      shadowRef.current = host.attachShadow({ mode: 'open' });
    }
    shadowRef.current.innerHTML = html;

    // The Unlayer export wraps every piece in a `min-height: 100vh` root div — meant to
    // fill the standalone page it was designed against, meaningless once that div is
    // embedded inline in a reading view. Left alone it forces roughly a viewport's worth
    // of empty space after the last paragraph before anything below the article (the
    // related-items list) becomes reachable. It's an inline style, so only another
    // inline edit can clear it — there's nothing to override from a stylesheet.
    shadowRef.current.querySelectorAll<HTMLElement>('[style*="vh"]').forEach((el) => {
      if (/vh/.test(el.style.minHeight)) {
        el.style.minHeight = '';
      }
    });

    // Same story, different property: the export also centers the body text in a
    // `.container` div capped at the editor's own email-safe width (500px), so the
    // paragraphs read narrower than the full-width figure above them — the width was
    // sized for a standalone template, not for sitting under an image that already
    // fills this reading column. Uncapping it lets the text match the figure's width.
    shadowRef.current.querySelectorAll<HTMLElement>('.container').forEach((el) => {
      if (el.style.maxWidth) {
        el.style.maxWidth = '100%';
      }
    });
  }, [html]);

  return <div ref={hostRef} />;
};

export default ShadowContent;
